import { useState, useCallback, useEffect } from 'react';
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useUpdateProjectStatus,
} from '../../../../hooks/useProjects';
import { useSubscribers } from '../../../../hooks/useSubscribers';
import { useTaskTemplates, useTaskTemplatesByType } from '../../../../hooks/useTaskTemplates';
import { useAssignTemplatesToProject } from '../../../../hooks/useProjects';

/**
 * Hook personalizado para la gestión completa de proyectos
 * Migrado desde la lógica de DocAssemble con funcionalidad moderna
 * Maneja todo el estado y lógica del dashboard de proyectos
 */
export const useProjectManagement = () => {
  // Estados locales
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressDialogActiveTab, setProgressDialogActiveTab] = useState(0);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Estado del snackbar para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Datos del formulario
  const [formData, setFormData] = useState({
    uuid_contract: null,
    name: '',
    description: '',
    status: 'not_started',
    start_date: '',
    end_date: '',
    progress_template: '', // Template de progreso obligatorio
    quality_template: '', // Template de calidad obligatorio
  });

  // Hooks de TanStack Query para datos
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: subscribers = [], isLoading: subscribersLoading } = useSubscribers();
  const { data: taskTemplates = [] } = useTaskTemplates();
  const { data: progressTemplates = [] } = useTaskTemplatesByType('progress');
  const { data: qualityTemplates = [] } = useTaskTemplatesByType('quality');
  console.log(projects);
  // Hooks de mutaciones
  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const deleteProjectMutation = useDeleteProject();
  const updateStatusMutation = useUpdateProjectStatus();
  const assignTemplatesMutation = useAssignTemplatesToProject();

  // Filtrar proyectos por término de búsqueda
  const filteredProjects = projects.filter((project) => {
    if (!searchTerm) return true;

    const searchTermLower = searchTerm.toLowerCase();

    // Buscar por nombre del proyecto
    const nameMatch = project.name?.toLowerCase().includes(searchTermLower);

    // Buscar por descripción del proyecto
    const descriptionMatch = project.description?.toLowerCase().includes(searchTermLower);

    // Buscar por número de contrato
    const contractNumberMatch = project.contract?.contract_number
      ?.toLowerCase()
      .includes(searchTermLower);

    // Buscar por suscriptores del contrato
    let subscribersMatch = false;
    if (project.contract?.subscribers) {
      subscribersMatch = project.contract.subscribers.some((subscriber) => {
        const subscriberFirstName = subscriber.first_name?.toLowerCase() || '';
        const subscriberLastName = subscriber.last_name?.toLowerCase() || '';
        const fullName = `${subscriberFirstName} ${subscriberLastName}`.trim().toLowerCase();

        return (
          subscriberFirstName.includes(searchTermLower) ||
          subscriberLastName.includes(searchTermLower) ||
          fullName.includes(searchTermLower)
        );
      });
    }

    const result = nameMatch || descriptionMatch || contractNumberMatch || subscribersMatch;
    console.log(`Search result for "${searchTerm}":`, result);

    return result;
  });

  // Paginación
  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / rowsPerPage));
  const totalCount = filteredProjects.length;
  const paginatedProjects = filteredProjects.slice(
    (page - 1) * rowsPerPage,
    (page - 1) * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  // Calcular estadísticas del dashboard
  const projectStats = {
    total: projects.length,
    notStarted: projects.filter((p) => p.status === 'not_started').length,
    inProgress: projects.filter((p) => p.status === 'in_progress').length,
    completed: projects.filter((p) => p.status === 'completed').length,
    cancelled: projects.filter((p) => p.status === 'cancelled').length,
    completionRate:
      projects.length > 0
        ? (
            (projects.filter((p) => p.status === 'completed').length / projects.length) *
            100
          ).toFixed(1)
        : 0,
  };

  // Funciones de manejo de búsqueda
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  // Funciones de paginación
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 500);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  };

  // Funciones de diálogos principales
  const handleOpenDialog = () => {
    setEditMode(false);
    setSelectedProject(null);
    setFormData({
      project_number: '',
      name: '',
      description: '',
      status: 'not_started',
      construction_type: 'not_assigned',
      start_date: '',
      end_date: '',
      subscribers: [],
      progress_template: '',
      quality_template: '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProject(null);
    setFormData({
      project_number: '',
      name: '',
      description: '',
      status: 'not_started',
      construction_type: 'not_assigned',
      start_date: '',
      end_date: '',
      subscribers: [],
      progress_template: '',
      quality_template: '',
    });
  };

  // Funciones de diálogos específicos
  const handleOpenProgressDialog = (project) => {
    setSelectedProject(project);
    setProgressDialogOpen(true);
    setProgressDialogActiveTab(0);
  };

  const handleCloseProgressDialog = () => {
    setProgressDialogOpen(false);
    setSelectedProject(null);
    setProgressDialogActiveTab(0);
  };

  const handleOpenTemplatesDialog = (project) => {
    setSelectedProject(project);
    setTemplatesDialogOpen(true);
  };

  const handleCloseTemplatesDialog = () => {
    setTemplatesDialogOpen(false);
    setSelectedProject(null);
  };

  // Funciones de formulario
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (projectData) => {
    const dataToSave = projectData || formData;

    // Solo validar templates en modo creación, no en edición
    if (!editMode && (!dataToSave.progress_template || !dataToSave.quality_template)) {
      return;
    }

    try {
      if (editMode && selectedProject) {
        // En modo edición, solo enviar los campos editables (no templates)
        const { progress_template, quality_template, project_task_assignments, ...editableData } =
          dataToSave;

        await updateProjectMutation.mutateAsync({
          id: selectedProject.uuid,
          data: editableData,
        });
      } else {
        // En modo creación, incluir templates
        const {
          progress_template,
          quality_template,
          project_subscribers: subscribersData,
          subscribers,
          ...projectData
        } = dataToSave;

        const subscribersList = subscribersData || subscribers || [];

        const project_subscribers = subscribersList.map((subscriber) => ({
          uuid_subscriber: subscriber.uuid || subscriber.uuid_subscriber || subscriber,
          role: subscriber.role || 'subscriber',
          assigned_date: new Date().toISOString().split('T')[0],
        }));

        const template_uuids = [progress_template, quality_template].filter(Boolean);

        const cleanedProjectData = {
          ...projectData,
        };

        if (!cleanedProjectData.end_date || cleanedProjectData.end_date.trim() === '') {
          delete cleanedProjectData.end_date;
        }

        const unifiedProjectData = {
          ...cleanedProjectData,
          project_subscribers,
          template_uuids,
        };

        await createProjectMutation.mutateAsync(unifiedProjectData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar proyecto:', error);
    }
  };

  const handleEdit = (project) => {
    setEditMode(true);
    setSelectedProject(project);

    const projectProgressTemplate =
      project.assigned_templates?.find((t) => t.template_type === 'progress')?.uuid || '';
    const projectQualityTemplate =
      project.assigned_templates?.find((t) => t.template_type === 'quality')?.uuid || '';

    setFormData({
      project_number: project.project_number || '',
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'not_started',
      construction_type: project.construction_type || 'not_assigned',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      subscribers: project.subscribers || [],
      progress_template: projectProgressTemplate,
      quality_template: projectQualityTemplate,
    });
    setDialogOpen(true);
  };

  const handleDelete = (project) => {
    setProjectToDelete(project);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (projectToDelete) {
      try {
        await deleteProjectMutation.mutateAsync(projectToDelete.uuid);
        setDeleteConfirmOpen(false);
        setProjectToDelete(null);
      } catch (error) {
        console.error('Error al eliminar proyecto:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setProjectToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleStatusChange = async (project, newStatus) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: project.uuid,
        status: newStatus,
      });
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };

  const handleConstructionTypeChange = async (project, newConstructionType) => {
    try {
      await updateProjectMutation.mutateAsync({
        id: project.uuid,
        data: { construction_type: newConstructionType },
      });
    } catch (error) {
      console.error('Error al cambiar tipo de construcción:', error);
    }
  };

  const handleViewProgress = (project) => {
    setSelectedProject(project);
    setProgressDialogOpen(true);
    setProgressDialogActiveTab(0); // Tab de progreso
  };

  const handleViewQuality = (project) => {
    setSelectedProject(project);
    setProgressDialogOpen(true);
    setProgressDialogActiveTab(1); // Tab de calidad
  };

  const handleAssignTemplates = (project) => {
    handleOpenTemplatesDialog(project);
  };

  const handleAssignTemplatesConfirm = async (project, templateUuids) => {
    try {
      await assignTemplatesMutation.mutateAsync({
        id: project.uuid,
        templateUuids,
      });
      handleCloseTemplatesDialog();
    } catch (error) {
      console.error('Error al asignar templates:', error);
    }
  };

  // Loading state
  const isLoading = projectsLoading || subscribersLoading;

  return {
    // Data principal
    projects: paginatedProjects,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,

    // Estados de diálogos
    dialogOpen,
    progressDialogOpen,
    progressDialogActiveTab,
    templatesDialogOpen,
    editMode,
    selectedProject,

    // Datos de soporte
    subscribers,
    taskTemplates,
    progressTemplates,
    qualityTemplates,

    // Estadísticas del dashboard
    projectStats,

    // Funciones de manejo
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleOpenProgressDialog,
    handleCloseProgressDialog,
    handleOpenTemplatesDialog,
    handleCloseTemplatesDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleStatusChange,
    handleConstructionTypeChange,
    handleViewProgress,
    handleViewQuality,
    handleAssignTemplates,
    handleAssignTemplatesConfirm,

    // Diálogos de confirmación
    deleteConfirmOpen,
    projectToDelete,
    handleConfirmDelete,
    handleCancelDelete,

    // Notificaciones
    snackbar,
    handleCloseSnackbar,

    // Form data
    formData,
  };
};
